import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Send, Loader2, User, Search, Clock, Star, LogOut } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface ChatSession {
  id: string;
  guest_name: string;
  guest_email: string;
  user_id: string;
  status: string;
  rating?: number;
  feedback?: string;
  created_at: string;
  updated_at: string;
  last_message?: string;
}

interface Message {
  id: string;
  session_id: string;
  content: string;
  is_admin: boolean;
  created_at: string;
}

export function AdminSupportChat() {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [selectedSession, setSelectedSession] = useState<ChatSession | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    fetchSessions();

    const channel = supabase
      .channel('admin_chat_updates')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'chat_sessions' },
        (payload) => {
          console.log('Session change received:', payload);
          const updatedSession = payload.new as ChatSession;
          
          // Force update the selected session if it's the one that changed
          setSelectedSession(current => {
            if (current && updatedSession.id === current.id) {
              return { ...current, ...updatedSession };
            }
            return current;
          });
          
          fetchSessions();
        }
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'chat_messages' },
        (payload) => {
          const newMessage = payload.new as Message;
          if (selectedSession && newMessage.session_id === selectedSession.id) {
            setMessages((prev) => [...prev, newMessage]);
          }
          fetchSessions(); // Refresh list to show last message
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedSession]);

  const fetchSessions = async () => {
    const { data, error } = await (supabase as any)
      .from('chat_sessions' as any)
      .select('*, chat_messages(content, created_at)')
      .order('updated_at', { ascending: false });

    if (error) {
      toast.error('Failed to load chat sessions');
    } else {
      const formatted = data.map((s: any) => ({
        ...s,
        last_message: s.chat_messages?.[s.chat_messages.length - 1]?.content || 'New session'
      }));
      setSessions(formatted);
    }
    setLoading(false);
  };

  const fetchMessages = async (sid: string) => {
    const { data, error } = await (supabase as any)
      .from('chat_messages' as any)
      .select('*')
      .eq('session_id', sid)
      .order('created_at', { ascending: true });

    if (!error && data) {
      setMessages(data);
    }
  };

  const handleSelectSession = (session: ChatSession) => {
    setSelectedSession(session);
    fetchMessages(session.id);
  };

  const handleSend = async () => {
    if (!inputValue.trim() || !selectedSession || sending) return;

    setSending(true);
    const { error } = await (supabase as any).from('chat_messages' as any).insert({
      session_id: selectedSession.id,
      content: inputValue.trim(),
      is_admin: true,
    });

    if (error) {
      toast.error('Failed to send reply');
    } else {
      setInputValue('');
      // Update session timestamp
      await (supabase as any)
        .from('chat_sessions' as any)
        .update({ updated_at: new Date().toISOString() })
        .eq('id', selectedSession.id);
    }
    setSending(false);
  };

  const filteredSessions = sessions.filter(s => 
    s.guest_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.guest_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[600px]">
      {/* Sessions List */}
      <Card className="md:col-span-1 flex flex-col overflow-hidden">
        <CardHeader className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search chats..."
              className="pl-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </CardHeader>
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {loading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : filteredSessions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                No active support chats
              </div>
            ) : (
              filteredSessions.map((session) => (
                <button
                  key={session.id}
                  onClick={() => handleSelectSession(session)}
                  className={cn(
                    "w-full text-left p-3 rounded-lg transition-all border border-transparent",
                    selectedSession?.id === session.id
                      ? "bg-primary/10 border-primary/20"
                      : "hover:bg-muted"
                  )}
                >
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-bold text-sm truncate">{session.guest_name || 'Guest'}</span>
                    <span className="text-[10px] text-muted-foreground">
                      {format(new Date(session.updated_at), 'HH:mm')}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{session.last_message}</p>
                </button>
              ))
            )}
          </div>
        </ScrollArea>
      </Card>

      {/* Chat Area */}
      <Card className="md:col-span-2 flex flex-col overflow-hidden">
        {selectedSession ? (
          <>
            <CardHeader className="p-4 border-b flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">{selectedSession.guest_name || 'Guest'}</CardTitle>
                <CardDescription className="text-xs">{selectedSession.guest_email || 'No email provided'}</CardDescription>
                {selectedSession.rating && (
                  <div className="flex items-center gap-1 mt-1">
                    <div className="flex text-yellow-500">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={cn("h-3 w-3", i < selectedSession.rating! && "fill-current")} />
                      ))}
                    </div>
                    {selectedSession.feedback && (
                      <span className="text-[10px] text-muted-foreground italic">"{selectedSession.feedback}"</span>
                    )}
                  </div>
                )}
              </div>
              <Badge 
                variant="outline" 
                className={cn(
                  selectedSession.status === 'active' ? "bg-green-500/10 text-green-600 border-green-500/20" : "bg-muted text-muted-foreground"
                )}
              >
                {selectedSession.status === 'active' ? 'Active' : 'Ended'}
              </Badge>
            </CardHeader>
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={cn(
                      "flex flex-col max-w-[80%]",
                      msg.is_admin ? "self-end items-end ml-auto" : "self-start"
                    )}
                  >
                    <div
                      className={cn(
                        "px-4 py-2 rounded-2xl text-sm",
                        msg.is_admin
                          ? "bg-primary text-primary-foreground rounded-tr-none"
                          : "bg-muted text-foreground rounded-tl-none"
                      )}
                    >
                      {msg.content}
                    </div>
                    <span className="text-[10px] text-muted-foreground mt-1">
                      {format(new Date(msg.created_at), 'HH:mm')}
                    </span>
                  </div>
                ))}
                <div ref={messagesEndRef} />
                
                {selectedSession.status === 'ended' && (
                  <div className="flex flex-col items-center justify-center py-8 border-t border-dashed mt-4 space-y-3 bg-muted/20 rounded-xl">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest font-bold">
                      <LogOut className="h-3 w-3" />
                      User Ended Chat
                    </div>
                    
                    {selectedSession.rating ? (
                      <div className="flex flex-col items-center animate-in fade-in zoom-in duration-500">
                        <div className="flex text-yellow-500 mb-2">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className={cn("h-5 w-5", i < selectedSession.rating! && "fill-current")} />
                          ))}
                        </div>
                        {selectedSession.feedback && (
                          <p className="text-sm text-center px-6 italic text-muted-foreground">
                            "{selectedSession.feedback}"
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground italic">Waiting for review...</p>
                    )}
                  </div>
                )}
              </div>
            </ScrollArea>
            <div className="p-4 border-t bg-muted/30">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSend();
                }}
                className="flex gap-2"
              >
                <Input
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Type a reply..."
                  className="bg-background"
                  disabled={sending || selectedSession.status === 'ended'}
                />
                <Button size="icon" type="submit" disabled={!inputValue.trim() || sending || selectedSession.status === 'ended'}>
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </form>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
              <User className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="font-bold text-lg mb-1">NexQR Support Center</h3>
            <p className="text-muted-foreground text-sm max-w-xs">
              Select a session from the list to start chatting with a user.
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}
